import { useEffect, useId, useRef, useState } from "react";
import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { t } from "./i18n.js";
import { useEu } from "./Sessao.js";

/**
 * Seletor de empresa no TOPO da lateral.
 *
 * Fica aqui e não no menu de conta porque a empresa é CONTEXTO de tudo que
 * está na tela, não uma preferência da pessoa. Trocar de empresa troca cada
 * número do painel; trocar de tema, não.
 *
 * É também onde o mercado brasileiro deixou um buraco: Feedz não tem troca,
 * Convenia e Pontomais tratam filial como filtro, e a Sólides só oferece
 * troca dentro do portal do contador. Multi-CNPJ aqui é requisito, não extra.
 */
export function SeletorDeEmpresa({ recolhido }: { recolhido: boolean }) {
  const eu = useEu();
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const gatilho = useRef<HTMLButtonElement>(null);
  const id = useId();

  const atual = eu.empresas.find((x) => x.companyId === eu.empresaAtual);
  const varias = eu.empresas.length > 1;

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) =>
      caixa.current && !caixa.current.contains(e.target as Node) && setAberto(false);
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setAberto(false); gatilho.current?.focus();
    };
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", tecla);
    };
  }, [aberto]);

  const trocar = (companyId: string) => {
    if (companyId === eu.empresaAtual) { setAberto(false); return; }
    localStorage.setItem("eop:empresa", companyId);
    // Recarrega em vez de refazer o estado: trocar de empresa invalida TODO o
    // cache em memória, e sobrar uma tela com dado da empresa anterior é o
    // vazamento que a RLS impede no banco e a SPA reintroduziria na tela.
    window.location.assign("/");
  };

  const conteudo = (
    <>
      {/* Inicial da empresa, não o símbolo da casa: a marca já está na linha
          de cima, e repetir a mesma forma em 60px é ruído. */}
      <span aria-hidden className="eop-empresa-marca">
        {(atual?.nome ?? "?").trim().charAt(0).toUpperCase()}
      </span>
      {!recolhido && (
        <>
          <span className="eop-empresa-texto">
            <span className="eop-empresa-nome">{atual?.nome ?? t("app.nome")}</span>
            <span className="eop-empresa-papel">
              {atual?.papel ?? t("conta.semEmpresa")}
            </span>
          </span>
          {varias && <ChevronsUpDown aria-hidden size={14} className="eop-empresa-seta2" />}
        </>
      )}
    </>
  );

  // Com UMA empresa não vira botão: gatilho que abre lista de um item só
  // promete escolha e entrega nada.
  if (!varias) {
    return (
      <div className="eop-empresa-topo" title={recolhido ? atual?.nome : undefined}>
        {conteudo}
      </div>
    );
  }

  return (
    <div className="eop-seletor" ref={caixa}>
      <button
        ref={gatilho} type="button" className="eop-empresa-topo"
        aria-haspopup="menu" aria-expanded={aberto}
        aria-controls={aberto ? id : undefined}
        onClick={() => setAberto((v) => !v)}
        title={recolhido ? (atual?.nome ?? t("conta.empresas")) : undefined}
      >
        {conteudo}
      </button>

      {aberto && (
        <div className="eop-suspenso eop-suspenso-baixo" role="menu" id={id}
             aria-label={t("conta.empresas")}>
          <div className="eop-suspenso-rotulo eyebrow">{t("conta.empresas")}</div>
          {eu.empresas.map((emp) => (
            <button
              key={emp.companyId} type="button" role="menuitemradio"
              aria-checked={emp.companyId === eu.empresaAtual}
              className="eop-suspenso-item" onClick={() => trocar(emp.companyId)}
            >
              <Building2 aria-hidden size={15} />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{emp.nome}</span>
                <span className="eop-suspenso-sub">{emp.papel}</span>
              </span>
              {emp.companyId === eu.empresaAtual && (
                <Check aria-hidden size={14} className="eop-suspenso-marca" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
